<template>
  <div class="common-course-status">
    <div class="common-course-status__box-headline">
      {{ runCourse.masterCode }}, {{ runCourse.title }}<span v-if="runCourse.creditPoints > 0">&nbsp;({{ runCourse.creditPoints }} {{ $t('course.creditPoints') }})</span>
    </div>

    <div class="common-course-status__classroom">
      <strong>
        {{ runCourse.code }}
      </strong>

      ({{ formattedDate(runCourse.startDate) }} - {{ formattedDate(runCourse.endDate) }})
    </div>

    <div
      v-if="activeUnit && activeUnitIndex >= 0"
      class="common-course-status__unit">
      <span v-if="activeUnitIndex >= 1">
        {{
          $t('common.courseStatus.unitsComplete', {
            currentUnit: activeUnitIndex,
            totalUnits: runCourse.units.length
          })
        }}
      </span>
      <JwlButton
        v-if="activeUnitIndex < runCourse.units.length"
        class="common-course-status unit-button"
        :to-route="{ name: 'unit_overview', params: { unit: activeUnit.code, concentration: programmeCode } }">
        {{ $t('common.courseStatus.openUnit', {currentUnit: activeUnit.shortTitle}) }}
      </JwlButton>
    </div>
  </div>
</template>

<script setup>
  const props = defineProps({
    runCourse: Object,
    programmeCode: String,
  });

  const route = useRoute();

  const { currentUser } = useModuleStore();

  const formattedDate = (dateString) => {
    const date = new Date(dateString);
    const lang = route.params.locale === 'en' ? 'en-GB' : route.params.locale;
    return date.toLocaleDateString(lang, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const activeUnitIndex = computed(() => {
    if (!props.runCourse) {
      return -1;
    }

    const now = new Date();
    return props.runCourse.units.findIndex((unit) => {
      const unitStart = new Date(unit.startDate);
      const unitEnd = new Date(unit.endDate);
      return unitStart < now && unitEnd > now;
    });
  });

  const activeUnit = computed(() => {
    if (!props.runCourse || activeUnitIndex.value < 0) {
      return null;
    }

    return props.runCourse.units[activeUnitIndex.value];
  });
</script>

<style lang="scss">
@use "@base/styles/mixins";
@use "@base/styles/variables";

.common-course-status {
  align-items: flex-start;
  background-color: var(--course-status-color);
  border-radius: var(--border-radius);
  box-sizing: border-box;
  color: var(--color-white);
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  padding: 1rem 1.25rem;
  width: 100%;

  &:not(:last-child) {
    margin-bottom: 1rem;
  }

  &__headline {
    font-size: 1.25rem;
    margin-bottom: .5rem;
    text-transform: uppercase;
  }

  &__box-headline {
    display: flex;
    font-size: 1.3rem;
    margin-bottom: .5rem;

    @include mixins.mq(variables.$mq-phone) {
      font-size: 1rem;
    }
  }

  &__subheadline {
    display: flex;
    font-weight: variables.$font-weight-light;
    margin-bottom: .5rem;

    @include mixins.mq(variables.$mq-phone) {
      font-size: .9rem;
    }
  }

  &__classroom {
    font-weight: variables.$font-weight-light;

    @include mixins.mq(variables.$mq-phone) {
      font-size: .9rem;
    }
  }

  &__progress {
    align-items: center;
    display: flex;
    flex-flow: row nowrap;
    margin-bottom: 1rem;
    margin-top: auto;
    width: 100%;
  }

  &__progress-bar {
    @include mixins.ltrtl(margin-right, margin-left, 1rem);

    align-items: stretch;
    background-color: var(--color-gray-200);
    border-radius: .175rem;
    display: flex;
    height: .35rem;
    justify-content: flex-start;
    overflow: hidden;
    width: 100%;
  }

  &__progress-fill {
    background-color: currentColor;
    border-radius: .175rem;
    height: 100%;
  }

  &__unit {
    align-items: center;
    border-top: 1px solid var(--color-white);
    display: flex;
    justify-content: space-between;
    margin-top: .75rem;
    padding-top: .75rem;
    width: 100%;

    @include mixins.mq(variables.$mq-phone) {
      align-items: stretch;
      flex-flow: column;
      width: 100%;

      span {
        display: inline-block;
        margin-bottom: .75rem;
      }
    }
  }
}

.unit-button {
  @include mixins.ltrtl(margin-left, margin-right, auto);

  --button-color: var(--course-status-color) !important;

  border-color: var(--color-white) !important;
}
</style>
